import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import Select from 'react-select';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'remixicon/fonts/remixicon.css';

const selectStyles = {
    control: (base) => ({
        ...base,
        height: '50px',
        minHeight: '50px',
        borderRadius: '0.375rem',
        borderColor: '#d9dee3',
        fontSize: '0.9375rem',
        boxShadow: 'none',
        '&:hover': { borderColor: '#b2b5c0' }
    }),
    valueContainer: (base) => ({
        ...base,
        padding: '14px 14px 0 14px',
        height: '48px',
        overflow: 'hidden'
    }),
    placeholder: (base) => ({
        ...base,
        margin: 0,
        padding: 0,
        color: '#a1acb8'
    }),
    singleValue: (base) => ({
        ...base,
        margin: 0,
        padding: 0,
        color: '#566a7f'
    }),
    input: (base) => ({
        ...base,
        margin: '0',
        padding: '0'
    }),
    indicatorsContainer: (base) => ({
        ...base,
        height: '48px'
    }),
    menu: (base) => ({
        ...base,
        zIndex: 9999,
        fontSize: '0.9375rem'
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? '#0b5cab' : state.isFocused ? '#f5f5f9' : 'white',
        color: state.isSelected ? 'white' : '#566a7f',
        cursor: 'pointer',
    })
};

const SearchableSelect = ({ label, options, value, onChange, isDisabled, placeholder, selectStyles: ignoredSelectStyles }) => {
    const [isFocused, setIsFocused] = useState(false);
    const hasValue = value !== null && value !== undefined && value !== '';
    const shouldFloat = isFocused || hasValue;

    return (
        <div className="position-relative" style={{ minHeight: '50px', height: '50px' }}>
            <Select
                options={options}
                value={value}
                onChange={onChange}
                isDisabled={isDisabled}
                placeholder={isFocused ? placeholder : ""}
                isSearchable
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                styles={selectStyles}
            />
            <label
                style={{
                    position: 'absolute',
                    left: '10px',
                    top: shouldFloat ? '-8px' : '14px',
                    fontSize: shouldFloat ? '0.75rem' : '0.9375rem',
                    color: shouldFloat ? '#0b5cab' : '#8592a3',
                    pointerEvents: 'none',
                    transition: 'all 0.2s ease',
                    zIndex: 4,
                    backgroundColor: shouldFloat ? 'white' : 'transparent',
                    padding: '0 6px',
                    lineHeight: '1'
                }}
            >
                {label}
            </label>
        </div>
    );
};

const riskOptions = [
    { value: 'Low', label: 'Low' },
    { value: 'Moderate', label: 'Moderate' },
    { value: 'High', label: 'High' }
];

const relationOptions = [
    { value: 'Father', label: 'Father' },
    { value: 'Mother', label: 'Mother' },
    { value: 'Brother', label: 'Brother' },
    { value: 'Sister', label: 'Sister' },
    { value: 'Spouse', label: 'Spouse' },
    { value: 'Other', label: 'Other' }
];

const serviceOptions = [
    { value: 'Residential Program', label: 'Residential Program' },
    { value: 'Day Rehab Program', label: 'Day Rehab Program' },
    { value: 'Home Visitation', label: 'Home Visitation' },
    { value: 'Therapy Services', label: 'Therapy Services' }
];

const DoctorOPDAssessmentForm = ({ initialData, onSubmit, isLoading, buttonText, patients = [], initialPatientId }) => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        // Section 1: Patient Information
        patient_id: '',
        comorbid: '',
        education: '',
        reference: '',
        duration_of_illness: '',
        consultant: '',
        // Section 2: Reason for Admission & Consent
        reason_for_admission: {
            acute_care: false,
            relapse: false,
            readmission_medical: false,
            respite_care: false,
            diagnostic_evaluation: false,
            for_rehab: false,
            clozapine_treatment: false,
            chronic_management: false
        },
        consent_taken_from: 'Both',
        // Section 3: Escort Details
        escort_reason: '',
        guardian_name: '',
        relation: '',
        // Section 4: Physical Examination & Vitals
        blood_pressure: '',
        temperature: '',
        pulse: '',
        respiratory_rate: '',
        weight: '',
        height: '',
        bmi: '',
        rbs: '',
        // Section 5: General Appearance
        ga_head: '',
        ga_throat: '',
        ga_skin: '',
        ga_lymphatic: '',
        ga_eyes: '',
        ga_neck: '',
        ga_respiratory: '',
        ga_musculoskeletal: '',
        ga_ear: '',
        ga_neurologic: '',
        ga_cardiovascular: '',
        ga_hygiene: '',
        ga_nose: '',
        ga_psychiatric: '',
        ga_gastrointestinal: '',
        // Section 6: Patient History
        presenting_complaint: '',
        history_present_illness: '',
        past_medical_history: '',
        past_psychiatric_history: '',
        past_surgical_history: '',
        family_history: '',
        social_history: '',
        allergies: '',
        // Section 7: Risk Assessment
        roh_risk: '',
        ros_risk: '',
        rov_risk: '',
        roe_risk: '',
        rof_risk: '',
        rovu_risk: '',
        // Section 8: Labs & Diagnosis (Repeater)
        labs_investigations: [{ id: Date.now(), test_name: '' }],
        provisional_diagnosis: '',
        actual_diagnosis: '',
        // Section 9: Treatment Plan
        pharmacotherapy: '',
        special_order: '',
        // Section 10: Consultant Review
        consultant_review: '',
        consultant_name: '',
        consultant_signature: '',
        consultant_date: '',
        consultant_time: '',
        is_referred: 'No',
        referral_service: ''
    });

    useEffect(() => {
        if (initialData) {
            setFormData(initialData);
        }
    }, [initialData]);

    useEffect(() => {
        if (initialPatientId) {
            setFormData(prev => ({ ...prev, patient_id: initialPatientId }));
        }
    }, [initialPatientId]);

    const patientOptions = patients.map(p => ({
        value: p.id,
        label: `${p.first_name || ''} ${p.last_name || ''} - ${p.cnic || 'No CNIC'}`
    }));

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox' && name.startsWith('reason_')) {
            const field = name.replace('reason_', '');
            setFormData(prev => ({
                ...prev,
                reason_for_admission: { ...prev.reason_for_admission, [field]: checked }
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleRepeaterChange = (section, id, value) => {
        setFormData(prev => ({
            ...prev,
            [section]: prev[section].map(item => item.id === id ? { ...item, test_name: value } : item)
        }));
    };

    const addRow = (section) => {
        const newRow = { id: Date.now(), test_name: '' };
        setFormData(prev => ({ ...prev, [section]: [...prev[section], newRow] }));
    };

    const removeRow = (section, id) => {
        if (formData[section].length > 1) {
            setFormData(prev => ({ ...prev, [section]: prev[section].filter(item => item.id !== id) }));
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(formData);
    };

    return (
        <form onSubmit={handleSubmit}>
            <div className="accordion" id="doctorOPDAccordion">
                {/* 1. Patient Information */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold" type="button" data-bs-toggle="collapse" data-bs-target="#secPatientInfo">
                            1. Patient Information
                        </button>
                    </h2>
                    <div id="secPatientInfo" className="accordion-collapse collapse show">
                        <div className="accordion-body pt-3">
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <SearchableSelect
                                        label="Select Patient"
                                        options={patientOptions}
                                        value={patientOptions.find(opt => Number(opt.value) === Number(formData.patient_id)) || null}
                                        onChange={(opt) => setFormData(prev => ({ ...prev, patient_id: opt ? opt.value : "" }))}
                                        isDisabled={!!initialData}
                                        placeholder="Search and Select Patient..."
                                        selectStyles={selectStyles}
                                    />
                                </div>
                                {['comorbid', 'education', 'reference', 'duration_of_illness', 'consultant'].map(f => (
                                    <div className="col-md-6" key={f}>
                                        <div className="form-floating form-floating-outline">
                                            <input type="text" className="form-control" name={f} value={formData[f]} onChange={handleInputChange} placeholder={f.replace('_', ' ').toUpperCase()} />
                                            <label>{f.replace('_', ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</label>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* 2. Reason for Admission & Consent */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#secReason">
                            2. Reason for Admission & Consent
                        </button>
                    </h2>
                    <div id="secReason" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">
                            <p className="fw-bold mb-3 border-bottom pb-2">Reason for Admission:</p>
                            <div className="row g-2 mb-4">
                                {[
                                    { k: 'acute_care', l: 'Acute Care' },
                                    { k: 'relapse', l: 'Relapse' },
                                    { k: 'readmission_medical', l: 'Readmission After D/C Due to Medical Ground' },
                                    { k: 'respite_care', l: 'Respite Care' },
                                    { k: 'diagnostic_evaluation', l: 'For Diagnostic evaluation' },
                                    { k: 'for_rehab', l: 'For Rehab' },
                                    { k: 'clozapine_treatment', l: 'For Clozapine Treatment' },
                                    { k: 'chronic_management', l: 'Chronic Case Symptomatic Management' }
                                ].map(item => (
                                    <div className="col-md-6" key={item.k}>
                                        <div className="form-check">
                                            <input className="form-check-input" type="checkbox" name={`reason_${item.k}`} checked={formData.reason_for_admission[item.k]} onChange={handleInputChange} id={`chk_${item.k}`} />
                                            <label className="form-check-label" htmlFor={`chk_${item.k}`}>{item.l}</label>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <small className="d-block mb-1 fw-semibold">Consent Taken From:</small>
                            <div className="d-flex gap-3 mt-2 justify-content-center">
                                {['Patient', 'Family', 'Both'].map(val => (
                                    <div className="form-check" key={val}>
                                        <input className="form-check-input" type="radio" name="consent_taken_from" value={val} checked={formData.consent_taken_from === val} onChange={handleInputChange} />
                                        <label className="form-check-label">{val}</label>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* 3. Escort Details */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#secEscort">
                            3. Escort Details
                        </button>
                    </h2>
                    <div id="secEscort" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">
                            <div className="row g-3">
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="escort_reason" value={formData.escort_reason} onChange={handleInputChange} placeholder="Reason" />
                                        <label>Reason for Escort</label>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="guardian_name" value={formData.guardian_name} onChange={handleInputChange} placeholder="Guardian Name" />
                                        <label>Guardian Name</label>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <SearchableSelect
                                        label="Relation"
                                        options={relationOptions}
                                        value={relationOptions.find(opt => opt.value === formData.relation) || null}
                                        onChange={(opt) => setFormData(prev => ({ ...prev, relation: opt ? opt.value : "" }))}
                                        placeholder="Select Relation"
                                        selectStyles={selectStyles}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 4. Physical Examination & Vitals */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#secVitals">
                            4. Physical Examination & Vitals
                        </button>
                    </h2>
                    <div id="secVitals" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">
                            <div className="row g-3">
                                {['blood_pressure', 'temperature', 'pulse', 'respiratory_rate', 'weight', 'height', 'bmi', 'rbs'].map(f => (
                                    <div className="col-md-3" key={f}>
                                        <div className="form-floating form-floating-outline">
                                            <input type="text" className="form-control" name={f} value={formData[f]} onChange={handleInputChange} placeholder={f.replace('_', ' ').toUpperCase()} />
                                            <label>{f.replace('_', ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</label>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* 5. General Appearance */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#secGA">
                            5. General Appearance
                        </button>
                    </h2>
                    <div id="secGA" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">
                            <div className="row g-3">
                                {[
                                    'head', 'throat', 'skin', 'lymphatic', 'eyes', 'neck', 'respiratory',
                                    'musculoskeletal', 'ear', 'neurologic', 'cardiovascular', 'hygiene',
                                    'nose', 'psychiatric', 'gastrointestinal'
                                ].map(f => (
                                    <div className="col-md-4" key={f}>
                                        <div className="form-floating form-floating-outline">
                                            <input type="text" className="form-control" name={`ga_${f}`} value={formData[`ga_${f}`]} onChange={handleInputChange} placeholder={f.toUpperCase()} />
                                            <label>{f.charAt(0).toUpperCase() + f.slice(1)}</label>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* 6. Patient History */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#secHistory">
                            6. Patient History
                        </button>
                    </h2>
                    <div id="secHistory" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">
                            <div className="row g-3">
                                <div className="col-md-12">
                                    <div className="form-floating form-floating-outline">
                                        <textarea className="form-control h-px-100" name="presenting_complaint" value={formData.presenting_complaint} onChange={handleInputChange} placeholder="Complaint"></textarea>
                                        <label>Presenting Complaint</label>
                                    </div>
                                </div>
                                <div className="col-md-12">
                                    <div className="form-floating form-floating-outline">
                                        <textarea className="form-control h-px-100" name="history_present_illness" value={formData.history_present_illness} onChange={handleInputChange} placeholder="History"></textarea>
                                        <label>History of Present Illness</label>
                                    </div>
                                </div>
                                {[
                                    'past_medical_history', 'past_psychiatric_history', 'past_surgical_history',
                                    'family_history', 'social_history', 'allergies'
                                ].map(f => (
                                    <div className="col-md-6" key={f}>
                                        <div className="form-floating form-floating-outline">
                                            <input type="text" className="form-control" name={f} value={formData[f]} onChange={handleInputChange} placeholder={f.replace(/_/g, ' ').toUpperCase()} />
                                            <label>{f.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</label>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* 7. Risk Assessment */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#secRisks">
                            7. Risk Assessment
                        </button>
                    </h2>
                    <div id="secRisks" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">
                            <div className="row g-3">
                                {[
                                    { k: 'roh_risk', l: 'ROH (Risk of Homicidal)' },
                                    { k: 'ros_risk', l: 'ROS (Risk of Suicide)' },
                                    { k: 'rov_risk', l: 'ROV (Risk of Violence)' },
                                    { k: 'roe_risk', l: 'ROE (Risk of Escape)' },
                                    { k: 'rof_risk', l: 'ROF (Risk of Falls)' },
                                    { k: 'rovu_risk', l: 'ROVU (Risk of Vulnerability)' }
                                ].map(item => (
                                    <div className="col-md-4" key={item.k}>
                                        <SearchableSelect
                                            label={item.l}
                                            options={riskOptions}
                                            value={riskOptions.find(opt => opt.value === formData[item.k]) || null}
                                            onChange={(opt) => setFormData(prev => ({ ...prev, [item.k]: opt ? opt.value : "" }))}
                                            placeholder="Select Risk"
                                            selectStyles={selectStyles}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* 8. Labs & Diagnosis */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#secLabs">
                            8. Labs & Diagnosis
                        </button>
                    </h2>
                    <div id="secLabs" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">
                            <p className="fw-bold mb-3">Labs Investigation:</p>
                            {formData.labs_investigations.map((row, index) => (
                                <div className="row g-2 mb-2 align-items-center" key={row.id}>
                                    <div className="col">
                                        <div className="form-floating form-floating-outline">
                                            <input type="text" className="form-control" value={row.test_name} onChange={(e) => handleRepeaterChange('labs_investigations', row.id, e.target.value)} placeholder="Lab Test" />
                                            <label>Lab Test {index + 1}</label>
                                        </div>
                                    </div>
                                    <div className="col-auto">
                                        <button type="button" className="btn btn-outline-danger btn-icon" onClick={() => removeRow('labs_investigations', row.id)}>
                                            <i className="ri-delete-bin-line"></i>
                                        </button>
                                    </div>
                                </div>
                            ))}
                            <button type="button" className="btn btn-sm btn-outline-primary mt-2 mb-4" onClick={() => addRow('labs_investigations')}>
                                <i className="ri-add-line me-1"></i> Add Lab Test
                            </button>
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="provisional_diagnosis" value={formData.provisional_diagnosis} onChange={handleInputChange} placeholder="Provisional" />
                                        <label>Provisional Diagnosis</label>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="actual_diagnosis" value={formData.actual_diagnosis} onChange={handleInputChange} placeholder="Actual" />
                                        <label>Actual Diagnosis</label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 9. Treatment Plan */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#secPlan">
                            9. Treatment Plan
                        </button>
                    </h2>
                    <div id="secPlan" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">
                            <div className="row g-3">
                                <div className="col-md-12">
                                    <div className="form-floating form-floating-outline">
                                        <textarea className="form-control h-px-100" name="pharmacotherapy" value={formData.pharmacotherapy} onChange={handleInputChange} placeholder="Pharmacotherapy"></textarea>
                                        <label>Pharmacotherapy</label>
                                    </div>
                                </div>
                                <div className="col-md-12">
                                    <div className="form-floating form-floating-outline">
                                        <textarea className="form-control h-px-100" name="special_order" value={formData.special_order} onChange={handleInputChange} placeholder="Special Order"></textarea>
                                        <label>Special Order</label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 10. Consultant Review */}
                <div className="accordion-item mb-3 shadow-sm rounded">
                    <h2 className="accordion-header">
                        <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#secReview">
                            10. Consultant Review
                        </button>
                    </h2>
                    <div id="secReview" className="accordion-collapse collapse">
                        <div className="accordion-body pt-3">
                            <div className="row g-3">
                                <div className="col-md-12">
                                    <div className="form-floating form-floating-outline">
                                        <textarea className="form-control h-px-100" name="consultant_review" value={formData.consultant_review} onChange={handleInputChange} placeholder="Review"></textarea>
                                        <label>Consultant Review or Note</label>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="consultant_name" value={formData.consultant_name} onChange={handleInputChange} placeholder="Name" />
                                        <label>Consultant Name</label>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <input type="text" className="form-control" name="consultant_signature" value={formData.consultant_signature} onChange={handleInputChange} placeholder="Signature" />
                                        <label>Signature / Stamp</label>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <input type="date" className="form-control" name="consultant_date" value={formData.consultant_date} onChange={handleInputChange} />
                                        <label>Date</label>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-floating form-floating-outline">
                                        <input type="time" className="form-control" name="consultant_time" value={formData.consultant_time} onChange={handleInputChange} />
                                        <label>Time</label>
                                    </div>
                                </div>

                                <div className="col-md-12 mt-3 p-3 bg-light rounded shadow-sm border">
                                    <div className="d-flex flex-wrap align-items-center gap-4">
                                        <div>
                                            <label className="fw-bold me-3">Do you want to refer?</label>
                                            <div className="form-check form-check-inline">
                                                <input className="form-check-input" type="radio" name="is_referred" id="referYesDoctor" value="Yes" checked={formData.is_referred === 'Yes'} onChange={handleInputChange} />
                                                <label className="form-check-label" htmlFor="referYesDoctor">Yes</label>
                                            </div>
                                            <div className="form-check form-check-inline">
                                                <input className="form-check-input" type="radio" name="is_referred" id="referNoDoctor" value="No" checked={formData.is_referred === 'No'} onChange={handleInputChange} />
                                                <label className="form-check-label" htmlFor="referNoDoctor">No</label>
                                            </div>
                                        </div>

                                        {formData.is_referred === 'Yes' && (
                                            <div className="flex-grow-1" style={{ minWidth: '250px' }}>
                                                <SearchableSelect
                                                    label="Select Service"
                                                    options={serviceOptions}
                                                    value={serviceOptions.find(opt => opt.value === formData.referral_service) || null}
                                                    onChange={(opt) => setFormData(prev => ({ ...prev, referral_service: opt ? opt.value : "" }))}
                                                    placeholder="Select Service"
                                                    selectStyles={selectStyles}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-5 pt-3 border-top d-flex gap-2">
                <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/doctor-opd-assessments')}>
                    <i className="ri-arrow-left-line me-1"></i>Back
                </button>
                <button type="submit" className="btn btn-primary sticky-form-submit" disabled={isLoading}>
                    {isLoading ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                            Processing...
                        </>
                    ) : (
                        <>
                            <i className="ri-save-line me-1"></i>{buttonText || 'Save Assessment'}
                        </>
                    )}
                </button>
            </div>
        </form>
    );
};

export default DoctorOPDAssessmentForm;
